import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, Observer, Subject, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs';
import { TypeaheadMatch, TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { TenantService } from 'src/app/shared/api-services/tenant.service';
import { HeadquartersService } from 'src/app/shared/api-services/headquarters.service';
import { HeadquarterResponse } from 'src/app/shared/models/headquarter/headquarter-response';
import { DropdownOption } from 'src/app/shared/components/dropdown/dropdown-primary/dropdown.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GenericFilter } from 'src/app/shared/components/modals/filter-modal/generic-filter';
import { DropdownModule } from 'src/app/shared/components/dropdown/dropdown.module';
import { CommonModule } from '@angular/common';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

export enum ReportFilterType {
  START_DATE = 'start-date',
  END_DATE = 'end-date',
  START_MILEAGE = 'start-mileage',
  END_MILEAGE = 'end-mileage',
  START_COST = 'start-cost',
  END_COST = 'end-cost',

  HEADQUARTER_ID = 'headquarter-id',
  VEHICLE_TYPE_ID = 'vehicle-type-id',
}

@Component({
    selector: 'modal-reports-filter',
    imports: [CommonModule, TranslateModule, ReactiveFormsModule, FormsModule, TypeaheadModule, BsDatepickerModule, DropdownModule],
    templateUrl: './modal-reports-filter.component.html',
    styleUrls: ['./modal-reports-filter.component.scss'],
})
export class ModalReportsFilterComponent implements OnInit, OnDestroy {
  @Input() id = 'filter-reports-modal';
  form: FormGroup;

  filterSelected: DropdownOption;
  typeFilter: string;
  listFilterOption: DropdownOption[] = [];

  mainFilter: GenericFilter;

  @Input() autoCompleteParams: any = {};
  headquarterName$: Observable<string[]>;

  private _destroy$ = new Subject<void>();

  constructor(
    private _headquartersService: HeadquartersService,
    private _translateService: TranslateService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _toastr: ToastrService,
    public bsModalRef: BsModalRef
  ) {
    this.form = new FormGroup({
      typeMileage: new FormControl(null),

      startMileage: new FormControl(null),
      endMileage: new FormControl(null),

      startDate: new FormControl(null),
      endDate: new FormControl(null),

      startCost: new FormControl(null),
      endCost: new FormControl(null),

      headquarterId: new FormControl(null),
      headquarterName: new FormControl(null),

      vehicleTypeId: new FormControl(null),
      vehicleTypeName: new FormControl(null),
    });
  }

  ngOnInit() {
    this.autoComplete();
    this.initFilterOptions();
  }

  private initFilterOptions() {
    this.listFilterOption = this._translateService.instant('REPORTS.REPORT_FILTER.LIST_FILTERS');
    if (this.listFilterOption?.length > 0) {
      this.filterSelected = JSON.parse(JSON.stringify(this.listFilterOption[0]));
      this.typeFilter = this.filterSelected.value;
    };
  }

  onFilterSelected($event: DropdownOption) {
    this.typeFilter = $event.value;
  }

  private autoComplete() {
    this.headquarterName$ = new Observable((observer: Observer<string | undefined>) => {
      observer.next(this.form.controls['headquarterName'].value);
    }).pipe(
      debounceTime(500),
      takeUntil(this._destroy$),
      distinctUntilChanged(),
      switchMap((token: string) => this._headquartersService.listHeadquarters$({ nameContains: token, limit: 5, ...this.autoCompleteParams })),
      map((response: HeadquarterResponse) => {
        let displayNames: string[] = [];
        response.items.forEach((headquarter) => {
          displayNames.push(headquarter.name);
        });
        return displayNames;
      }),
    );
  }

  onVehicleTypeSelected($event: DropdownOption) {
    this.form.patchValue({
      vehicleTypeId: $event.value,
      vehicleTypeName: $event.text
    });
  }

  onSelectHeadquarter(match: TypeaheadMatch) {
    const name = match.value;
    this._headquartersService.listHeadquarters$({ name: name })
    .pipe(takeUntil(this._destroy$))
    .subscribe({
      next: (response) => {
        if (response.items.length > 0) {
          this.form.patchValue({
            headquarterId: response.items[0].id,
            headquarterName: response.items[0].name,
          });
        }
      }
    });
  }

  selectRadioMileage(typeMileage: string) {
    if (typeMileage === 'before-100k') {
      this.form.patchValue({
        startMileage: 0,
        endMileage: 100000
      });
    }

    if (typeMileage === 'after-100k') {
      this.form.patchValue({
        startMileage: 100000,
        endMileage: null
      });
    }

    if (typeMileage === 'range-km') {
      this.form.patchValue({
        startMileage: null,
        endMileage: null
      });
    }
  }

  private isValidDateRange(): boolean {
    const startDate = this.form.get('startDate')?.value;
    const endDate = this.form.get('endDate')?.value;

    if (startDate && endDate) {
      // Calcola la differenza in millisecondi
      const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
      // Converti in giorni
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      // Controlla se la differenza è maggiore di 365 giorni
      return diffDays <= 365;
    }
    return true;
  }

  apply() {
    if (!this.isValidDateRange()) {
      this._toastr.error(this._translateService.instant('REPORTS.REPORT_FILTER.DATE_RANGE_ERROR'));
      return;
    }

    // add query params to the url without overwriting existing ones
    const formValues = this.form.value;
    const currentParams = { ...this._route.snapshot.queryParams };
    const newParams = { ...currentParams };
    
    // Add headquarter params if present
    if (formValues.headquarterId && formValues.headquarterName) {
      newParams[ReportFilterType.HEADQUARTER_ID] = formValues.headquarterId;
    }
    
    // Add vehicle type params if present
    if (formValues.vehicleTypeId && formValues.vehicleTypeName) {
      newParams[ReportFilterType.VEHICLE_TYPE_ID] = formValues.vehicleTypeId;
    }
    
    // Add date range params if present
    if (formValues.startDate) {
      newParams[ReportFilterType.START_DATE] = formValues.startDate.toISOString();
    }
    
    if (formValues.endDate) {
      newParams[ReportFilterType.END_DATE] = formValues.endDate.toISOString();
    }
    
    // Add mileage params if present
    if (formValues.startMileage !== null && formValues.startMileage !== undefined) {
      newParams[ReportFilterType.START_MILEAGE] = formValues.startMileage;
    }
    
    if (formValues.endMileage !== null && formValues.endMileage !== undefined) {
      newParams[ReportFilterType.END_MILEAGE] = formValues.endMileage;
    }
    
    // Update URL with new params
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: newParams,
      queryParamsHandling: 'merge'
    });

    this.bsModalRef.hide();
  }

  reset() {
    this.form.reset();
    this.bsModalRef.hide();
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }
}